import React, { useState } from 'react';
import { AlertTriangle, Search, Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { runLeaveAudit } from '../../services/api';

const LeaveAuditDialog = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('');

  const handleRun = async () => {
    setLoading(true);
    try {
      const res = await runLeaveAudit();
      setData(res.data);
    } catch {
      setData({ flags: [], total_flagged: 0, employees_checked: 0, error: true });
    }
    setLoading(false);
  };

  const filtered = data?.flags?.filter(f =>
    !filter || f.employee_code.toLowerCase().includes(filter.toLowerCase()) ||
    f.employee_name.toLowerCase().includes(filter.toLowerCase()) ||
    f.department.toLowerCase().includes(filter.toLowerCase())
  ) || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="audit-dialog-overlay">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col" data-testid="audit-dialog">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-semibold">Leave Balance Audit</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-audit-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {!data && !loading && (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">This audit checks all employees across all years to flag cases where annual leave taken exceeds entitlement.</p>
              <p className="text-sm text-gray-400 mb-6">Excess leave should either be recorded as LOP or marked as advance payment by the company.</p>
              <Button onClick={handleRun} data-testid="run-audit-btn">
                <Search className="w-4 h-4 mr-2" />
                Run Audit
              </Button>
            </div>
          )}

          {loading && (
            <div className="text-center py-16">
              <Loader2 className="w-8 h-8 text-blue-500 mx-auto mb-3 animate-spin" />
              <p className="text-gray-500">Auditing all employees...</p>
            </div>
          )}

          {data && !loading && (
            <>
              {/* Summary */}
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg px-4 py-2 text-sm">
                  <span className="text-gray-500">Checked:</span>{' '}
                  <span className="font-medium">{data.employees_checked} employees</span>
                </div>
                <div className={`rounded-lg px-4 py-2 text-sm ${data.total_flagged > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                  <span className="text-gray-500">Flagged:</span>{' '}
                  <span className={`font-medium ${data.total_flagged > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {data.total_flagged} issues
                  </span>
                </div>
                {data.total_flagged > 0 && (
                  <input
                    type="text"
                    placeholder="Filter by name, code, dept..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="ml-auto border rounded-lg px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-300"
                    data-testid="audit-filter-input"
                  />
                )}
              </div>

              {data.total_flagged === 0 ? (
                <div className="text-center py-10">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Search className="w-6 h-6 text-green-500" />
                  </div>
                  <p className="text-green-700 font-medium">All clear! No excess leave found.</p>
                  <p className="text-sm text-gray-400 mt-1">Every employee's annual leave is within entitlement across all years.</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm" data-testid="audit-results-table">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                        <th className="px-4 py-2.5">Employee</th>
                        <th className="px-3 py-2.5">Dept</th>
                        <th className="px-3 py-2.5 text-center">Year</th>
                        <th className="px-3 py-2.5 text-right">Entitled</th>
                        <th className="px-3 py-2.5 text-right">CF</th>
                        <th className="px-3 py-2.5 text-right">Available</th>
                        <th className="px-3 py-2.5 text-right">Taken</th>
                        <th className="px-3 py-2.5 text-right">Excess</th>
                        <th className="px-3 py-2.5">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filtered.map((f, i) => (
                        <tr key={i} className="hover:bg-red-50/40" data-testid={`audit-row-${f.employee_code}-${f.year}`}>
                          <td className="px-4 py-2.5">
                            <span className="font-medium text-gray-800">{f.employee_code}</span>
                            <span className="text-gray-400 ml-1.5">{f.employee_name}</span>
                          </td>
                          <td className="px-3 py-2.5 text-gray-500">{f.department}</td>
                          <td className="px-3 py-2.5 text-center font-medium">{f.year}</td>
                          <td className="px-3 py-2.5 text-right">{f.entitlement}</td>
                          <td className="px-3 py-2.5 text-right">{f.carry_forward}</td>
                          <td className="px-3 py-2.5 text-right">{f.total_available}</td>
                          <td className="px-3 py-2.5 text-right font-medium">{f.taken}</td>
                          <td className="px-3 py-2.5 text-right">
                            <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-medium">
                              +{f.excess} excess
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${f.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                              {f.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-4 flex justify-between items-center">
                <p className="text-xs text-gray-400">
                  Excess days should be recorded as LOP/unpaid, or marked as advance payment if the company paid for them.
                </p>
                <Button variant="outline" size="sm" onClick={handleRun} data-testid="rerun-audit-btn">
                  Re-run
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveAuditDialog;
